import { useEffect, useState } from 'react';
import { Drawer, Form, Input, Upload, Image, Popconfirm } from 'antd';
import type { UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAbility } from '@casl/react';
import { Button, StatusBadge, AbilityContext } from 'app/atoms';
import { PermissionActions, PermissionSubjects } from 'configs/permissionConstants';
import {
  createContentItem,
  getContentItem,
  publishContentItem,
  unpublishContentItem,
  updateContentItem,
  uploadContentItemImage,
} from './service';
import { ContentItem, ContentItemPayload, ContentItemStatus } from './types';

const { TextArea } = Input;

interface ContentItemFormProps {
  visible: boolean;
  item: ContentItem | null;
  onClose: () => void;
}

const ContentItemForm: React.FC<ContentItemFormProps> = ({ visible, item, onClose }) => {
  const [form] = Form.useForm<ContentItemPayload>();
  const [currentItem, setCurrentItem] = useState<ContentItem | null>(item);
  const [uploadingCount, setUploadingCount] = useState(0);
  const queryClient = useQueryClient();
  const ability = useAbility(AbilityContext);
  const canPublish = ability.can(PermissionActions.PUBLISH, PermissionSubjects.CONTENT_ITEMS);

  useEffect(() => {
    if (visible) {
      setCurrentItem(item);
      form.setFieldsValue({ title: item?.title ?? '', bodyText: item?.bodyText ?? '' });
    }
  }, [item, form, visible]);

  const invalidateList = () => queryClient.invalidateQueries({ queryKey: ['content-items'] });

  const refreshCurrentItem = async (id: string) => {
    const fresh = await getContentItem(id);
    setCurrentItem(fresh);
  };

  const createMutation = useMutation({
    mutationFn: createContentItem,
    onSuccess: (created) => {
      toast.success('Onboarding screen created as a draft');
      setCurrentItem(created);
      invalidateList();
    },
    onError: () => toast.error('Failed to create onboarding screen'),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: ContentItemPayload) => updateContentItem(currentItem!.id, payload),
    onSuccess: (updated) => {
      toast.success('Changes saved');
      setCurrentItem(updated);
      invalidateList();
    },
    onError: () => toast.error('Failed to save changes'),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadContentItemImage(currentItem!.id, file),
    onSuccess: () => {
      toast.success('Image uploaded');
      invalidateList();
      if (currentItem) refreshCurrentItem(currentItem.id);
    },
    onError: () => toast.error('Failed to upload image - only JPEG/PNG files are accepted'),
    onSettled: () => setUploadingCount((count) => count - 1),
  });

  const handleUpload = (file: File) => {
    setUploadingCount((count) => count + 1);
    uploadMutation.mutate(file);
    return false;
  };

  const publishMutation = useMutation({
    mutationFn: () => publishContentItem(currentItem!.id),
    onSuccess: (updated) => {
      toast.success('Published to the live apps');
      setCurrentItem(updated);
      invalidateList();
    },
    onError: () => toast.error('Failed to publish'),
  });

  const unpublishMutation = useMutation({
    mutationFn: () => unpublishContentItem(currentItem!.id),
    onSuccess: (updated) => {
      toast.success('Unpublished');
      setCurrentItem(updated);
      invalidateList();
    },
    onError: () => toast.error('Failed to unpublish'),
  });

  const handleFinish = (values: ContentItemPayload) => {
    if (currentItem) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  const uploadProps: UploadProps = {
    accept: 'image/jpeg,image/png',
    multiple: true,
    showUploadList: false,
    disabled: !currentItem || uploadingCount > 0,
    beforeUpload: handleUpload,
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isUploading = uploadingCount > 0;

  return (
    <Drawer
      title={currentItem ? 'Edit Onboarding Screen' : 'New Onboarding Screen'}
      open={visible}
      onClose={onClose}
      width={480}
      destroyOnClose
      extra={
        currentItem && (
          <StatusBadge
            status={currentItem.status}
            label={currentItem.status === ContentItemStatus.PUBLISHED ? 'Published' : 'Draft'}
          />
        )
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item name="title" label="Heading" rules={[{ required: true, message: 'Heading is required' }]}>
          <Input placeholder="e.g. Welcome to Urban Mobility" />
        </Form.Item>
        <Form.Item name="bodyText" label="Text" rules={[{ required: true, message: 'Text is required' }]}>
          <TextArea rows={5} placeholder="Screen body copy shown to app users" />
        </Form.Item>

        <div className="mb-4">
          <p className="text-sm font-medium mb-2 font-figtree">Images</p>
          {currentItem && currentItem.imageUrls.length > 0 && (
            <div className="flex gap-2 flex-wrap mb-2">
              {currentItem.imageUrls.map((url) => (
                <Image key={url} src={url} width={96} height={96} className="object-cover rounded border" />
              ))}
            </div>
          )}
          <Upload {...uploadProps}>
            <Button variant="secondary" icon={<UploadOutlined />} loading={isUploading} disabled={!currentItem}>
              {currentItem ? 'Upload images' : 'Save details first to upload images'}
            </Button>
          </Upload>
        </div>

        <div className="flex justify-between items-center gap-3 mt-6">
          <div>
            {currentItem && canPublish && (
              currentItem.status === ContentItemStatus.PUBLISHED ? (
                <Popconfirm title="Unpublish this screen?" onConfirm={() => unpublishMutation.mutate()} okText="Yes" cancelText="No">
                  <Button variant="secondary" loading={unpublishMutation.isPending}>
                    Unpublish
                  </Button>
                </Popconfirm>
              ) : (
                <Popconfirm title="Publish this screen to the live apps?" onConfirm={() => publishMutation.mutate()} okText="Yes" cancelText="No">
                  <Button variant="secondary" loading={publishMutation.isPending}>
                    Publish
                  </Button>
                </Popconfirm>
              )
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button variant="primary" htmlType="submit" loading={isSaving}>
              {currentItem ? 'Save Changes' : 'Create Draft'}
            </Button>
          </div>
        </div>
      </Form>
    </Drawer>
  );
};

export default ContentItemForm;
